import { useEffect, useState } from 'react';
import { useConfig } from 'hooks/use-config';
import {
  getFileBlobUrl,
  resolveSourceFileOpenUrl,
} from 'services/storage-service';
import type { Attachment } from '../types/chat';
import {
  isOfficeDocumentAttachment,
  isPdfAttachment,
  isTextPreviewableAttachment,
} from '../utils/assistant-file-preview';

const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;

type AssistantFileContentState = {
  content: string;
  blobUrl: string;
  /** Public/SAS HTTPS URL for Office/Google embed viewers (not a blob: URL). */
  openUrl: string;
  isLoading: boolean;
  error: boolean;
  tooLarge: boolean;
};

const INITIAL_STATE: AssistantFileContentState = {
  content: '',
  blobUrl: '',
  openUrl: '',
  isLoading: false,
  error: false,
  tooLarge: false,
};

const ensurePdfMimeBlobUrl = async (blobUrl: string): Promise<string> => {
  const response = await fetch(blobUrl);
  if (!response.ok) return blobUrl;

  const blob = await response.blob();
  if (blob.type === 'application/pdf') return blobUrl;

  const typedBlob = new Blob([blob], { type: 'application/pdf' });
  return URL.createObjectURL(typedBlob);
};

export const useAssistantFileContent = (file: Attachment | null) => {
  const { configurationSettings } = useConfig();
  const [state, setState] = useState<AssistantFileContentState>(INITIAL_STATE);

  useEffect(() => {
    if (!file?.fileId) {
      setState(INITIAL_STATE);
      return;
    }

    let cancelled = false;
    let createdPdfBlobUrl: string | null = null;

    const load = async () => {
      setState({
        content: '',
        blobUrl: '',
        openUrl: '',
        isLoading: true,
        error: false,
        tooLarge: false,
      });

      try {
        const isPdf = isPdfAttachment(file.fileName, file.extension);
        const isOffice = isOfficeDocumentAttachment(file.fileName, file.extension);

        const [blobUrlResult, openUrlResult] = await Promise.all([
          getFileBlobUrl(file.fileId, configurationSettings, undefined),
          // Office/PDF embeds need a real HTTPS URL; blob: URLs cannot be used by Google/Office viewers.
          isPdf || isOffice
            ? resolveSourceFileOpenUrl(file.fileId, configurationSettings, undefined)
            : Promise.resolve({ openUrl: undefined as string | undefined }),
        ]);

        if (!blobUrlResult && !openUrlResult.openUrl) {
          if (!cancelled) {
            setState({ ...INITIAL_STATE, error: true });
          }
          return;
        }

        let blobUrl = blobUrlResult || '';
        if (isPdf && blobUrl) {
          const typedUrl = await ensurePdfMimeBlobUrl(blobUrl);
          if (typedUrl !== blobUrl) {
            createdPdfBlobUrl = typedUrl;
            blobUrl = typedUrl;
          }
        }

        if (!isTextPreviewableAttachment(file.fileName, file.extension)) {
          if (!cancelled) {
            setState({
              content: '',
              blobUrl,
              openUrl: openUrlResult.openUrl || '',
              isLoading: false,
              error: false,
              tooLarge: false,
            });
          }
          return;
        }

        if (!blobUrl) {
          if (!cancelled) {
            setState({ ...INITIAL_STATE, error: true });
          }
          return;
        }

        const response = await fetch(blobUrl);
        if (!response.ok) {
          throw new Error(`Failed to read file content (${response.status})`);
        }

        const blob = await response.blob();
        if (blob.size > MAX_PREVIEW_BYTES) {
          if (!cancelled) {
            setState({
              content: '',
              blobUrl,
              openUrl: openUrlResult.openUrl || '',
              isLoading: false,
              error: false,
              tooLarge: true,
            });
          }
          return;
        }

        const content = await blob.text();
        if (!cancelled) {
          setState({
            content,
            blobUrl,
            openUrl: openUrlResult.openUrl || '',
            isLoading: false,
            error: false,
            tooLarge: false,
          });
        }
      } catch (error) {
        console.error('Failed to load assistant file preview:', error);
        if (!cancelled) {
          setState({ ...INITIAL_STATE, error: true });
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      if (createdPdfBlobUrl) {
        URL.revokeObjectURL(createdPdfBlobUrl);
      }
    };
  }, [file?.fileId, file?.fileName, file?.extension, configurationSettings]);

  return state;
};
